/**
 * Fila de prospecção — contrato compartilhado entre cliente e servidor.
 *
 * O problema que isto resolve: o SDR aborda dezenas de leads por dia pelo
 * WhatsApp pessoal e só quem agenda reunião chegava ao CRM. Aqui vivem as
 * regras puras (cadência, balde da fila, roteiro, link do WhatsApp) para que
 * a tela e o servidor calculem a MESMA coisa e os testes rodem sem banco.
 */

import { z } from "zod";

// ─── Configuração do funil de prospecção ─────────────────────────────────────

export const DEFAULT_INTERVALS_DAYS = [2, 3];
export const DEFAULT_MAX_ATTEMPTS = 3;

export const prospectingStageIdsSchema = z.object({
  queued: z.number().int(),
  contacted: z.number().int(),
  replied: z.number().int(),
  meeting: z.number().int(),
  lost: z.number().int(),
});

export const prospectingLossReasonIdsSchema = z.object({
  noReply: z.number().int(),
  notInterested: z.number().int(),
  invalidNumber: z.number().int(),
});

export const prospectingConfigSchema = z.object({
  enabled: z.boolean().default(true),
  /**
   * Dias entre a tentativa N e a N+1. `[2, 3]` = 2ª tentativa dois dias
   * depois da 1ª, 3ª tentativa três dias depois da 2ª. Se faltar valor, o
   * último se repete.
   */
  intervalsDays: z.array(z.number().int().min(1).max(60)).max(10),
  maxAttempts: z.number().int().min(1).max(10),
  stageIds: prospectingStageIdsSchema,
  lossReasonIds: prospectingLossReasonIdsSchema,
  /** Para onde vai a oportunidade de VENDA quando o lead agenda reunião. */
  salesPipelineId: z.number().int().nullable().default(null),
  salesStageId: z.number().int().nullable().default(null),
});

export type ProspectingConfig = z.infer<typeof prospectingConfigSchema>;

export const PROSPECTING_OUTCOMES = [
  "replied",
  "meeting",
  "no_reply",
  "not_interested",
  "invalid_number",
] as const;
export type ProspectingOutcome = (typeof PROSPECTING_OUTCOMES)[number];

export const PROSPECTING_OUTCOME_LABELS: Record<ProspectingOutcome, string> = {
  replied: "Respondeu",
  meeting: "Reunião agendada",
  no_reply: "Sem resposta",
  not_interested: "Sem interesse",
  invalid_number: "Número inválido",
};

// ─── Cadência ────────────────────────────────────────────────────────────────

/** Intervalo (dias) entre a tentativa `attemptNo` e a seguinte. */
export function intervalAfterAttempt(
  attemptNo: number,
  config: Pick<ProspectingConfig, "intervalsDays">,
): number {
  const list = config.intervalsDays.length ? config.intervalsDays : DEFAULT_INTERVALS_DAYS;
  const idx = Math.max(0, Math.min(attemptNo - 1, list.length - 1));
  return list[idx];
}

/**
 * Quando é a próxima tentativa depois de registrar a de número `attemptNo`.
 * `null` = esgotou (era a última permitida).
 */
export function computeNextAttempt(
  attemptNo: number,
  config: Pick<ProspectingConfig, "intervalsDays" | "maxAttempts">,
  now: Date,
): Date | null {
  if (attemptNo >= config.maxAttempts) return null;
  const days = intervalAfterAttempt(attemptNo, config);
  return new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
}

/**
 * Depois da última tentativa, quantos dias esperar antes de oferecer
 * "marcar como sem resposta". Reusa o último intervalo da cadência: é o mesmo
 * tempo que se daria para o lead responder a qualquer outra mensagem.
 */
export function graceDaysAfterLastAttempt(
  config: Pick<ProspectingConfig, "intervalsDays" | "maxAttempts">,
): number {
  return intervalAfterAttempt(config.maxAttempts, config);
}

// ─── Baldes da fila ──────────────────────────────────────────────────────────

export const QUEUE_BUCKETS = ["atrasados", "hoje", "novos", "aguardando", "esgotados"] as const;
export type QueueBucket = (typeof QUEUE_BUCKETS)[number];

export const QUEUE_BUCKET_LABELS: Record<QueueBucket, string> = {
  atrasados: "Atrasados",
  hoje: "Follow-up hoje",
  novos: "Novos",
  aguardando: "Aguardando",
  esgotados: "Esgotados",
};

/** "YYYY-MM-DD" da data no fuso — comparar dias sem se enrolar com offset. */
export function dateKeyInTimezone(date: Date, timezone: string): string {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(date);
}

export type QueueDealState = {
  attemptCount: number;
  lastAttemptAt: Date | null;
  nextAttemptAt: Date | null;
};

/**
 * Em que balde da fila a oportunidade cai.
 *
 *  - novos: nunca abordada
 *  - atrasados / hoje / aguardando: pela data da próxima tentativa
 *  - esgotados: já fez todas as tentativas e ninguém respondeu
 */
export function bucketOf(
  deal: QueueDealState,
  config: Pick<ProspectingConfig, "maxAttempts">,
  now: Date,
  timezone: string,
): QueueBucket {
  if (deal.attemptCount <= 0) return "novos";
  if (deal.attemptCount >= config.maxAttempts) return "esgotados";
  // Sem próxima data mas ainda com tentativas sobrando: a cadência foi
  // aumentada depois de esgotar. Volta para o trabalho, como atrasado.
  if (!deal.nextAttemptAt) return "atrasados";
  const today = dateKeyInTimezone(now, timezone);
  const next = dateKeyInTimezone(deal.nextAttemptAt, timezone);
  if (next < today) return "atrasados";
  if (next === today) return "hoje";
  return "aguardando";
}

/** Esgotou E já passou o prazo de espera — pode virar "sem resposta". */
export function isReadyForNoReply(
  deal: QueueDealState,
  config: Pick<ProspectingConfig, "intervalsDays" | "maxAttempts">,
  now: Date,
): boolean {
  if (deal.attemptCount < config.maxAttempts || !deal.lastAttemptAt) return false;
  const graceMs = graceDaysAfterLastAttempt(config) * 24 * 60 * 60 * 1000;
  return deal.lastAttemptAt.getTime() + graceMs <= now.getTime();
}

/** Ordem de trabalho: primeiro o que já venceu, depois o de hoje, depois os novos. */
export const QUEUE_BUCKET_ORDER: Record<QueueBucket, number> = {
  atrasados: 0,
  hoje: 1,
  novos: 2,
  aguardando: 3,
  esgotados: 4,
};

// ─── Roteiro e link do WhatsApp ──────────────────────────────────────────────

export const SCRIPT_VARIABLES = [
  { key: "nome", label: "Primeiro nome do lead" },
  { key: "nome_completo", label: "Nome completo do lead" },
  { key: "empresa", label: "Empresa do lead" },
  { key: "vendedor", label: "Seu primeiro nome" },
] as const;

export type ScriptVars = {
  nome?: string | null;
  nome_completo?: string | null;
  empresa?: string | null;
  vendedor?: string | null;
};

export function firstName(name: string | null | undefined): string {
  return (name ?? "").trim().split(/\s+/)[0] ?? "";
}

/**
 * Substitui `{{chave}}` pelos valores. Variável sem valor vira vazio e o
 * espaço duplicado que sobra é normalizado — "Oi {{nome}}, tudo bem?" com nome
 * vazio vira "Oi , tudo bem?" → "Oi, tudo bem?".
 */
export function renderScript(body: string, vars: ScriptVars): string {
  const table: Record<string, string> = {
    nome: firstName(vars.nome ?? vars.nome_completo),
    nome_completo: (vars.nome_completo ?? vars.nome ?? "").trim(),
    empresa: (vars.empresa ?? "").trim(),
    vendedor: firstName(vars.vendedor),
  };
  return body
    .replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (_m, key: string) => table[key.toLowerCase()] ?? "")
    .replace(/[ \t]+([,.!?;:])/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

/** Só dígitos, com DDI 55 quando o número veio sem código de país. */
export function whatsappDigits(phone: string | null | undefined): string | null {
  const digits = String(phone ?? "").replace(/\D/g, "");
  if (!digits) return null;
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) return digits;
  if (digits.length === 10 || digits.length === 11) return `55${digits}`;
  // Fora do padrão BR: deixa passar como está se tiver tamanho de E.164.
  if (digits.length >= 8 && digits.length <= 15) return digits;
  return null;
}

/**
 * Abre direto no aplicativo do WhatsApp (Desktop ou celular) — regra da casa:
 * prospecção sempre pelo app, nunca pelo Web. `whatsapp://` pula a página
 * intermediária do api.whatsapp.com, que no desktop costuma cair no Web.
 * Sem o app instalado o navegador não tem para onde mandar e nada acontece.
 */
export function buildWaMeUrl(phone: string | null | undefined, text: string): string | null {
  const digits = whatsappDigits(phone);
  if (!digits) return null;
  const q = text.trim() ? `&text=${encodeURIComponent(text.trim())}` : "";
  return `whatsapp://send?phone=${digits}${q}`;
}

// ─── Roteiros por tentativa (opções para o SDR escolher) ─────────────────────

export type ScriptCandidate = { id: number; attemptNo: number | null; isDefault: boolean; archived?: boolean };

/**
 * Quais roteiros o SDR pode usar na tentativa N: só os marcados para N; se
 * não houver nenhum, os de "qualquer tentativa". Roteiro da 1ª nunca aparece
 * na 2ª. O SDR escolhe; o relatório por roteiro diz qual rendeu mais.
 */
export function scriptsForAttempt<T extends ScriptCandidate>(scripts: T[], attemptNo: number): T[] {
  const active = scripts.filter((s) => !s.archived);
  const exact = active.filter((s) => s.attemptNo === attemptNo);
  if (exact.length) return exact;
  return active.filter((s) => s.attemptNo == null);
}

// ─── Distribuição de donos ───────────────────────────────────────────────────

/** Rodízio simples: o i-ésimo lead vai para o i-ésimo dono, circular. */
export function pickOwnerRoundRobin(ownerUserIds: number[], index: number): number | null {
  if (ownerUserIds.length === 0) return null;
  return ownerUserIds[index % ownerUserIds.length];
}
